import React, { useState, useEffect } from "react";
import axios from "axios";
import "./GestioneUtenti.css";
import Lf23Loader from "../components/Lf23Loader";

const GestioneUtenti = () => {
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    // Stato gestione ruoli
    const [roles, setRoles] = useState([]);
    const [availablePermissions, setAvailablePermissions] = useState({});
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [roleName, setRoleName] = useState("");
    const [roleDisplayName, setRoleDisplayName] = useState("");
    const [roleDescription, setRoleDescription] = useState("");
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [roleMessage, setRoleMessage] = useState("");
    const [roleMessageType, setRoleMessageType] = useState("");
    const [savingRole, setSavingRole] = useState(false);

    // Stato cambio ruolo utente
    const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newUserRole, setNewUserRole] = useState("");
    const [editEmail, setEditEmail] = useState("");
    const [editInteractaUserId, setEditInteractaUserId] = useState("");
    const [interactaUsers, setInteractaUsers] = useState([]);
    const [loadingInteractaUsers, setLoadingInteractaUsers] = useState(false);
    const [savingUser, setSavingUser] = useState(false);
    const [editUserMessage, setEditUserMessage] = useState("");

    // Stato creazione utente (collapsible)
    const [showCreateUser, setShowCreateUser] = useState(false);

    // Stato reset password
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [resetPasswordUser, setResetPasswordUser] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [resetMessage, setResetMessage] = useState("");
    const [resetMessageType, setResetMessageType] = useState("");
    const [resettingPassword, setResettingPassword] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    const currentUserRole = localStorage.getItem("role") || "user";
    const isSuperAdmin = currentUserRole === "superadmin";

    const filteredUsers = isSuperAdmin
        ? users
        : users.filter(user => user.role !== "superadmin");

    useEffect(() => {
        fetchUsers();
        fetchRoles();
        fetchPermissions();
    }, []);

    useEffect(() => {
        if (roles.length > 0 && !role) {
            const nonAdminRoles = roles.filter(r => r.name !== "superadmin" && r.name !== "gestore_magazzino");
            if (nonAdminRoles.length > 0) {
                setRole(nonAdminRoles[0].name);
            }
        }
    }, [roles]);

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
    });

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API_URL}/auth/users`, getAuthHeaders());
            setUsers(res.data);
        } catch (err) {
            console.error("Errore nel caricamento utenti:", err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const res = await axios.get(`${API_URL}/roles`, getAuthHeaders());
            setRoles(res.data);
        } catch (err) {
            console.error("Errore nel caricamento ruoli:", err);
        }
    };

    const fetchPermissions = async () => {
        try {
            const res = await axios.get(`${API_URL}/roles/permissions`, getAuthHeaders());
            setAvailablePermissions(res.data);
        } catch (err) {
            console.error("Errore nel caricamento permessi:", err);
        }
    };

    const roleOptions = roles
        .filter(r => isSuperAdmin || r.name !== "superadmin")
        .map(r => ({ value: r.name, label: r.display_name, description: r.description }));

    const validateForm = () => {
        if (username.length < 3) {
            setMessage("Lo username deve essere di almeno 3 caratteri");
            setMessageType("error");
            return false;
        }
        if (password.length < 6) {
            setMessage("La password deve essere di almeno 6 caratteri");
            setMessageType("error");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsLoading(true);
        setMessage("");
        try {
            await axios.post(`${API_URL}/auth/register-admin`, { username, password, role, email }, getAuthHeaders());
            setMessage(`Utente '${username}' creato con successo!`);
            setMessageType("success");
            setUsername("");
            setEmail("");
            setPassword("");
            setShowCreateUser(false);
            fetchUsers();
        } catch (err) {
            setMessage(err.response?.data?.error || "Errore durante la creazione dell'utente");
            setMessageType("error");
        } finally {
            setIsLoading(false);
        }
    };

    const getRoleName = (roleValue) => {
        if (roleValue === "superadmin") return "Super Amministratore";
        const r = roles.find(r => r.name === roleValue);
        return r ? r.display_name : roleValue;
    };

    const getUserInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || name.substring(0, 2).toUpperCase();
    };

    const getPermCount = (r) => r.permissions ? r.permissions.filter(p => p !== null).length : 0;

    const getUserCountForRole = (roleName) => users.filter(u => u.role === roleName).length;

    const getRoleColor = (name) => {
        const colors = {
            superadmin: '#9333EA',
            admin: '#2E6DB1',
            officina: '#D97706',
            abiti: '#059669',
            gestore_magazzino: '#DC2626'
        };
        return colors[name] || '#6366F1';
    };

    // === Gestione Ruoli ===

    const openCreateRole = () => {
        setEditingRole(null);
        setRoleName("");
        setRoleDisplayName("");
        setRoleDescription("");
        setSelectedPermissions([]);
        setRoleMessage("");
        setShowRoleModal(true);
    };

    const openEditRole = (r) => {
        setEditingRole(r);
        setRoleName(r.name);
        setRoleDisplayName(r.display_name);
        setRoleDescription(r.description || "");
        setSelectedPermissions(r.permissions ? r.permissions.filter(p => p !== null) : []);
        setRoleMessage("");
        setShowRoleModal(true);
    };

    const togglePermission = (cardKey) => {
        setSelectedPermissions(prev =>
            prev.includes(cardKey) ? prev.filter(k => k !== cardKey) : [...prev, cardKey]
        );
    };

    const toggleSection = (section, cards) => {
        const sectionKeys = cards.map(c => c.card_key);
        const allSelected = sectionKeys.every(k => selectedPermissions.includes(k));
        if (allSelected) {
            setSelectedPermissions(prev => prev.filter(k => !sectionKeys.includes(k)));
        } else {
            setSelectedPermissions(prev => [...new Set([...prev, ...sectionKeys])]);
        }
    };

    const getSectionLabel = (section) => {
        const labels = { reportistica: "Reportistica", automazione: "Automazione", produttivita: "Produttività" };
        return labels[section] || section;
    };

    const handleSaveRole = async () => {
        if (!roleDisplayName.trim()) {
            setRoleMessage("Il nome visualizzato è obbligatorio");
            setRoleMessageType("error");
            return;
        }
        setSavingRole(true);
        setRoleMessage("");
        try {
            if (editingRole) {
                await axios.put(`${API_URL}/roles/${editingRole.id}`, {
                    display_name: roleDisplayName, description: roleDescription, permissions: selectedPermissions
                }, getAuthHeaders());
                setRoleMessage("Ruolo aggiornato con successo");
            } else {
                if (!roleName.trim()) {
                    setRoleMessage("Il nome tecnico è obbligatorio");
                    setRoleMessageType("error");
                    setSavingRole(false);
                    return;
                }
                await axios.post(`${API_URL}/roles`, {
                    name: roleName.toLowerCase().replace(/\s+/g, '_'),
                    display_name: roleDisplayName, description: roleDescription, permissions: selectedPermissions
                }, getAuthHeaders());
                setRoleMessage("Ruolo creato con successo");
            }
            setRoleMessageType("success");
            fetchRoles();
            setTimeout(() => setShowRoleModal(false), 1000);
        } catch (err) {
            setRoleMessage(err.response?.data?.error || "Errore durante il salvataggio");
            setRoleMessageType("error");
        } finally {
            setSavingRole(false);
        }
    };

    const handleDeleteRole = async (roleToDelete) => {
        if (!window.confirm(`Sei sicuro di voler eliminare il ruolo "${roleToDelete.display_name}"?`)) return;
        try {
            await axios.delete(`${API_URL}/roles/${roleToDelete.id}`, getAuthHeaders());
            fetchRoles();
        } catch (err) {
            alert(err.response?.data?.error || "Errore durante l'eliminazione");
        }
    };

    const openChangeRole = async (user) => {
        setSelectedUser(user);
        setNewUserRole(user.role);
        setEditEmail(user.email || "");
        setEditInteractaUserId(user.interacta_user_id ? String(user.interacta_user_id) : "");
        setEditUserMessage("");
        setShowChangeRoleModal(true);
        setLoadingInteractaUsers(true);
        try {
            const res = await axios.get(`${API_URL}/auth/interacta-users`, getAuthHeaders());
            setInteractaUsers(res.data);
        } catch (err) {
            console.error("Errore caricamento utenti Interacta:", err);
        } finally {
            setLoadingInteractaUsers(false);
        }
    };

    const handleChangeUserRole = async () => {
        if (!selectedUser || !newUserRole) return;
        setSavingUser(true);
        setEditUserMessage("");
        try {
            if (newUserRole !== selectedUser.role) {
                await axios.put(`${API_URL}/roles/change-user-role`, {
                    userId: selectedUser.id, newRole: newUserRole
                }, getAuthHeaders());
            }
            const normalizedEmail = editEmail.trim();
            if (normalizedEmail !== (selectedUser.email || "")) {
                await axios.put(`${API_URL}/auth/user-email`, {
                    userId: selectedUser.id, email: normalizedEmail
                }, getAuthHeaders());
            }
            const currentInteractaId = selectedUser.interacta_user_id ? String(selectedUser.interacta_user_id) : "";
            if (editInteractaUserId !== currentInteractaId) {
                await axios.put(
                    `${API_URL}/auth/users/${selectedUser.id}/interacta`,
                    { interacta_user_id: editInteractaUserId || null },
                    getAuthHeaders()
                );
            }
            fetchUsers();
            setShowChangeRoleModal(false);
        } catch (err) {
            setEditUserMessage(err.response?.data?.error || "Errore durante il salvataggio");
        } finally {
            setSavingUser(false);
        }
    };

    const openResetPassword = (user) => {
        setResetPasswordUser(user);
        setNewPassword("");
        setShowNewPassword(false);
        setResetMessage("");
        setShowResetPasswordModal(true);
    };

    const handleResetPassword = async () => {
        if (!resetPasswordUser || !newPassword) return;
        if (newPassword.length < 6) {
            setResetMessage("La password deve essere di almeno 6 caratteri");
            setResetMessageType("error");
            return;
        }
        setResettingPassword(true);
        setResetMessage("");
        try {
            await axios.put(`${API_URL}/auth/reset-password`, {
                userId: resetPasswordUser.id, newPassword
            }, getAuthHeaders());
            setResetMessage("Password reimpostata con successo");
            setResetMessageType("success");
            setTimeout(() => setShowResetPasswordModal(false), 1200);
        } catch (err) {
            setResetMessage(err.response?.data?.error || "Errore durante il reset della password");
            setResetMessageType("error");
        } finally {
            setResettingPassword(false);
        }
    };

    return (
        <div className="gestione-utenti-container">
            <header className="page-header">
                <div className="header-content">
                    <div className="header-title">
                        <h2>Gestione Utenze</h2>
                        <p className="header-subtitle">Amministra utenti, ruoli e permessi di accesso alla piattaforma</p>
                    </div>
                </div>
            </header>

            <div className="content-area">

                {/* ========== SEZIONE RUOLI ========== */}
                <div className="section-block">
                    <div className="section-title-row">
                        <div className="section-title-left">
                            <h3>Ruoli</h3>
                            <span className="section-badge">{roles.length}</span>
                        </div>
                    </div>

                    <div className="roles-grid">
                        {roles.map((r) => {
                            const permCount = getPermCount(r);
                            const userCount = getUserCountForRole(r.name);
                            const color = getRoleColor(r.name);

                            return (
                                <div key={r.id} className="role-card" style={{ '--role-color': color }}>
                                    <div className="role-card-top">
                                        <div className="role-card-icon" style={{ background: `${color}15`, color }}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                            </svg>
                                        </div>
                                        <div className="role-card-actions">
                                            <button className="role-action-btn" onClick={() => openEditRole(r)} title="Modifica">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                </svg>
                                            </button>
                                            {!r.is_system && (
                                                <button className="role-action-btn role-action-delete" onClick={() => handleDeleteRole(r)} title="Elimina">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6"/>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="role-card-body">
                                        <div className="role-card-name-row">
                                            <h4>{r.display_name}</h4>
                                            {r.is_system && <span className="tag-system">Sistema</span>}
                                        </div>
                                        <p className="role-card-desc">{r.description || "Nessuna descrizione"}</p>
                                    </div>
                                    <div className="role-card-footer">
                                        <div className="role-card-stat">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                                                <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                                            </svg>
                                            {permCount} card
                                        </div>
                                        <div className="role-card-stat">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                <circle cx="9" cy="7" r="4"/>
                                            </svg>
                                            {userCount} {userCount === 1 ? 'utente' : 'utenti'}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Card "+" per creare nuovo ruolo */}
                        <div className="role-card role-card-add" onClick={openCreateRole}>
                            <div className="role-card-add-content">
                                <div className="role-card-add-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19"/>
                                        <line x1="5" y1="12" x2="19" y2="12"/>
                                    </svg>
                                </div>
                                <span>Nuovo ruolo</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ========== SEZIONE UTENTI ========== */}
                <div className="section-block">
                    <div className="section-title-row">
                        <div className="section-title-left">
                            <h3>Utenti</h3>
                            <span className="section-badge">{filteredUsers.length}</span>
                        </div>
                        <button className="btn-add-user" onClick={() => setShowCreateUser(true)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <line x1="19" y1="8" x2="19" y2="14"/>
                                <line x1="22" y1="11" x2="16" y2="11"/>
                            </svg>
                            Nuovo utente
                        </button>
                    </div>

                    {/* Lista utenti */}
                    {loadingUsers ? (
                        <Lf23Loader />
                    ) : filteredUsers.length === 0 ? (
                        <div className="empty-state">
                            <p>Nessun utente trovato</p>
                        </div>
                    ) : (
                        <div className="user-list">
                            {filteredUsers.map((user) => (
                                <div key={user.id} className="user-item">
                                    <div className="user-item-left">
                                        <div className="user-item-avatar" style={{ background: `linear-gradient(135deg, ${getRoleColor(user.role)}, ${getRoleColor(user.role)}cc)` }}>
                                            {getUserInitials(user.username)}
                                        </div>
                                        <div className="user-item-info">
                                            <h4>{user.username}</h4>
                                            <span className="user-role-tag" style={{ color: getRoleColor(user.role), background: `${getRoleColor(user.role)}12` }}>
                                                {getRoleName(user.role)}
                                            </span>
                                            <span className="user-email-line">{user.email || "— nessuna email"}</span>
                                        </div>
                                    </div>
                                    <div className="user-item-right">
                                        {isSuperAdmin && (
                                            <button
                                                className="btn-reset-password"
                                                onClick={() => openResetPassword(user)}
                                                title="Reimposta password"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                                </svg>
                                                Reset password
                                            </button>
                                        )}
                                        <button
                                            className="btn-change-role"
                                            onClick={() => openChangeRole(user)}
                                            disabled={user.role === "superadmin" && !isSuperAdmin}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                            </svg>
                                            Modifica
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ========== MODALE CREA/MODIFICA RUOLO ========== */}
            {showRoleModal && (
                <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
                    <div className="modal-content role-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingRole ? "Modifica Ruolo" : "Crea Nuovo Ruolo"}</h3>
                            <button className="modal-close" onClick={() => setShowRoleModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="modal-form-row">
                                {!editingRole && (
                                    <div className="form-field">
                                        <label className="form-label">Nome tecnico</label>
                                        <input type="text" value={roleName} onChange={(e) => setRoleName(e.target.value)}
                                            className="form-input modal-input" placeholder="es. responsabile_zona" />
                                        <p className="field-hint">Identificativo univoco, senza spazi (usa underscore)</p>
                                    </div>
                                )}
                                <div className="form-field">
                                    <label className="form-label">Nome visualizzato</label>
                                    <input type="text" value={roleDisplayName} onChange={(e) => setRoleDisplayName(e.target.value)}
                                        className="form-input modal-input" placeholder="es. Responsabile di Zona" disabled={editingRole?.is_system} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Descrizione</label>
                                    <input type="text" value={roleDescription} onChange={(e) => setRoleDescription(e.target.value)}
                                        className="form-input modal-input" placeholder="Descrizione del ruolo" />
                                </div>
                            </div>

                            <div className="permissions-picker">
                                <h4 className="permissions-picker-title">Permessi Card</h4>
                                {editingRole && (editingRole.name === "admin" || editingRole.name === "superadmin") ? (
                                    <div className="permissions-locked-notice">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                        </svg>
                                        <span>
                                            I permessi di <strong>{editingRole.display_name}</strong> sono gestiti automaticamente.
                                            {editingRole.name === "superadmin"
                                                ? " Questo ruolo ha accesso a tutte le card, inclusa la reportistica."
                                                : " Questo ruolo ha accesso a tutte le card, esclusa la reportistica."}
                                            {" "}Nuove card vengono incluse automaticamente.
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        <p className="permissions-picker-subtitle">Seleziona le card che questo ruolo potrà visualizzare</p>

                                        {Object.entries(availablePermissions).map(([section, cards]) => {
                                            const sectionKeys = cards.map(c => c.card_key);
                                            const allSelected = sectionKeys.every(k => selectedPermissions.includes(k));
                                            const someSelected = sectionKeys.some(k => selectedPermissions.includes(k));

                                            return (
                                                <div key={section} className="permission-section">
                                                    <div className="permission-section-header">
                                                        <label className="permission-section-toggle">
                                                            <input type="checkbox" checked={allSelected}
                                                                ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                                                onChange={() => toggleSection(section, cards)} />
                                                            <span className="custom-checkbox"></span>
                                                            <span className="permission-section-name">{getSectionLabel(section)}</span>
                                                        </label>
                                                        <span className="permission-section-count">
                                                            {sectionKeys.filter(k => selectedPermissions.includes(k)).length}/{cards.length}
                                                        </span>
                                                    </div>
                                                    <div className="permission-cards-grid">
                                                        {cards.map((card) => (
                                                            <label key={card.card_key}
                                                                className={`permission-card ${selectedPermissions.includes(card.card_key) ? 'selected' : ''}`}>
                                                                <input type="checkbox" checked={selectedPermissions.includes(card.card_key)}
                                                                    onChange={() => togglePermission(card.card_key)} />
                                                                <span className="custom-checkbox"></span>
                                                                <div className="permission-card-info">
                                                                    <span className="permission-card-title">{card.title}</span>
                                                                    {card.tag_name && <span className="permission-card-badge" style={{ backgroundColor: card.tag_color, color: '#fff' }}>{card.tag_name}</span>}
                                                                </div>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>

                            {roleMessage && (
                                <div className={`message-alert ${roleMessageType}`} style={{ marginTop: 16 }}>
                                    <p>{roleMessage}</p>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowRoleModal(false)}>Annulla</button>
                            <button className="btn-submit" onClick={handleSaveRole} disabled={savingRole}>
                                {savingRole ? (<><div className="loading-spinner"></div> Salvataggio...</>) : (editingRole ? "Salva Modifiche" : "Crea Ruolo")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== MODALE CAMBIO RUOLO UTENTE ========== */}
            {showChangeRoleModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowChangeRoleModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 450 }}>
                        <div className="modal-header">
                            <h3>Modifica Utente — {selectedUser.username}</h3>
                            <button className="modal-close" onClick={() => setShowChangeRoleModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-field">
                                <label className="form-label">Nuovo ruolo</label>
                                <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} className="form-select modal-input">
                                    {roleOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-field">
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    value={editEmail}
                                    onChange={(e) => setEditEmail(e.target.value)}
                                    className="form-input modal-input"
                                    placeholder="nome.cognome@dominio.it"
                                />
                                <p className="field-hint">Usata per collegare il calendario Google. Lascia vuoto per rimuoverla.</p>
                            </div>
                            <div className="form-field">
                                <label className="form-label">Account Interacta</label>
                                <select
                                    value={editInteractaUserId}
                                    onChange={(e) => setEditInteractaUserId(e.target.value)}
                                    className="form-select modal-input"
                                    disabled={loadingInteractaUsers}
                                >
                                    <option value="">— Nessun account —</option>
                                    {interactaUsers.map((u) => (
                                        <option key={u.id} value={String(u.id)}>
                                            {u.lastName} {u.firstName} (ID: {u.id})
                                        </option>
                                    ))}
                                </select>
                                <p className="field-hint">Usato per pubblicare registrazioni call su Interacta a nome dell'utente.</p>
                            </div>
                            {editUserMessage && (
                                <div className="message-alert error" style={{ marginTop: 12 }}>
                                    <p>{editUserMessage}</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowChangeRoleModal(false)}>Annulla</button>
                            <button className="btn-submit" onClick={handleChangeUserRole} disabled={savingUser}>
                                {savingUser ? (<><div className="loading-spinner"></div> Salvataggio...</>) : "Salva"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== MODALE RESET PASSWORD ========== */}
            {showResetPasswordModal && resetPasswordUser && (
                <div className="modal-overlay" onClick={() => setShowResetPasswordModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 450 }}>
                        <div className="modal-header">
                            <h3>Reimposta Password — {resetPasswordUser.username}</h3>
                            <button className="modal-close" onClick={() => setShowResetPasswordModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-field">
                                <label className="form-label">Nuova password</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="form-input modal-input"
                                        placeholder="Inserisci la nuova password (min. 6 caratteri)"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                    >
                                        {showNewPassword ? (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                                <line x1="1" y1="1" x2="23" y2="23"/>
                                            </svg>
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                <circle cx="12" cy="12" r="3"/>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            {resetMessage && (
                                <div className={`message-alert ${resetMessageType}`} style={{ marginTop: 12 }}>
                                    <p>{resetMessage}</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowResetPasswordModal(false)}>Annulla</button>
                            <button className="btn-submit" onClick={handleResetPassword} disabled={resettingPassword || !newPassword}>
                                {resettingPassword ? (<><div className="loading-spinner"></div> Reimpostazione...</>) : "Reimposta Password"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== MODALE CREAZIONE UTENTE ========== */}
            {showCreateUser && (
                <div className="modal-overlay" onClick={() => setShowCreateUser(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
                        <div className="modal-header">
                            <h3>Crea Nuovo Utente</h3>
                            <button className="modal-close" onClick={() => { setShowCreateUser(false); setMessage(""); }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-field">
                                    <label className="form-label">Username</label>
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className="form-input modal-input"
                                        placeholder="Inserisci username"
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Email <span style={{ color: "#A0AEC0", fontWeight: 400 }}>(opzionale)</span></label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="form-input modal-input"
                                        placeholder="nome.cognome@dominio.it"
                                    />
                                    <p className="field-hint">Necessaria per collegare il calendario e pubblicare su Interacta a suo nome.</p>
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Password</label>
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="form-input modal-input"
                                            placeholder="Inserisci password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                                    <line x1="1" y1="1" x2="23" y2="23"/>
                                                </svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                    <circle cx="12" cy="12" r="3"/>
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Ruolo</label>
                                    <select
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                        className="form-select modal-input"
                                    >
                                        {roleOptions.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {message && (
                                    <div className={`message-alert ${messageType}`} style={{ marginTop: 12 }}>
                                        <p>{message}</p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => { setShowCreateUser(false); setMessage(""); }}>Annulla</button>
                                <button type="submit" className="btn-submit" disabled={isLoading}>
                                    {isLoading ? (<><div className="loading-spinner"></div> Creazione...</>) : "Crea Utente"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestioneUtenti;
